import {
  Form,
  ValidationContainer,
  ValidationMessage,
} from "@tandemdiabetes/sugarmate-react-components"
import escapeStringRegexp from "escape-string-regexp"
import { useCallback } from "react"
import type {
  ChangeEventHandler,
  FormEventHandler,
  ReactNode,
} from "react"

import styles from "../AccountForms.module.scss"

import {
  LargePasswordInput,
  PasswordRequirements,
} from "src/components"
import { usePolyglot } from "src/contexts"
import { passwordPattern } from "src/utils"

const polyglotPrefix = "components.account_forms.password."
const passwordId = "password-id"
const passwordConfirmationId = "password-confirmation-id"

const passwordErrorId = "password-error-id"
const passwordConfirmationErrorId = "password-confirmation-error-id"

interface PasswordInfo {
  password: string;
  passwordConfirmation: string;
}

interface Props {
  form: PasswordInfo;
  updateForm: (form: Partial<PasswordInfo>) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  children?: ReactNode;
}

export function PasswordForm(props: Props) {
  const {
    children,
    onSubmit,
    form,
    updateForm,
  } = props

  const polyglot = usePolyglot()

  const onPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        password: e.target.value,
      })
    },
    [updateForm],
  )

  const onPasswordConfirmationChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        passwordConfirmation: e.target.value,
      })
    },
    [updateForm],
  )

  const passwordConfirmationPattern = `^(${escapeStringRegexp(form.password)})?$`

  return (
    <Form
      className={styles.form}
      onSubmit={onSubmit}
    >
      <div className={styles.heading}>
        {polyglot.t(`${polyglotPrefix}title`)}
      </div>
      <PasswordRequirements />
      <div className={styles.inputs}>
        <ValidationContainer>
          <LargePasswordInput
            id={passwordId}
            placeholder={polyglot.t(`${polyglotPrefix}password.placeholder`)}
            className={styles.input}
            value={form.password}
            onChange={onPasswordChange}
            pattern={passwordPattern}
            autoComplete="new-password"
            minLength={8}
            required
          />
          <ValidationMessage
            id={passwordErrorId}
            invalidMessage={{
              valueMissing: polyglot.t("validation_message.value_missing"),
              tooShort: polyglot.t("validation_message.password_too_short"),
              patternMismatch: polyglot.t("validation_message.password_invalid"),
            }}
          />
        </ValidationContainer>
        <ValidationContainer>
          <LargePasswordInput
            id={passwordConfirmationId}
            placeholder={polyglot.t(`${polyglotPrefix}password_confirmation.placeholder`)}
            className={styles.input}
            value={form.passwordConfirmation}
            onChange={onPasswordConfirmationChange}
            pattern={passwordConfirmationPattern}
            autoComplete="new-password"
            required
          />
          <ValidationMessage
            id={passwordConfirmationErrorId}
            invalidMessage={{
              valueMissing: polyglot.t("validation_message.value_missing"),
              patternMismatch: polyglot.t("validation_message.password_mismatch"),
            }}
          />
        </ValidationContainer>
      </div>
      {children}
    </Form>
  )
}
